import type { ThemeConfig } from 'antd';
import type { TenantTheme } from './types';

/**
 * 从 :root 的 CSS variable 读取设计 token。
 * 切租户时 data-theme 变 → CSS variable 变 → 这里 getComputedStyle 拿到的就是新主题色。
 *
 * 注意：buildAntdTheme 每次 useTenant 触发都会被调一次（TenantThemeProvider useMemo 内），
 * 所以 CSS var 总是当前租户的最新值。
 *
 * Fallback 仅用于 SSR / 首屏 tokens.css 未加载的极端情况（CSR 下不会触发）。
 */
function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * AntD 5 ConfigProvider seed token — 映射 §1 设计 token。
 * 用 useTenant() 动态生成，运行时切租户即跟着变（CSS variable 是单一真相）。
 *
 * Sec Reviewer high#1 修正：之前 success/warning/error/info/bg 都硬编码圣丰联色，
 * xicheng 切换时 AntD 内部组件不跟租户走，破"a-lite 切租户 0 改代码"承诺。
 * 现全部走 cssVar()，单一真相在 tokens.css。
 */
export function buildAntdTheme(tenant: TenantTheme): ThemeConfig {
  return {
    cssVar: true,
    hashed: false,
    token: {
      colorPrimary: tenant.colorPrimary,
      colorSuccess: cssVar('--color-success', '#3B6D11'),
      colorWarning: cssVar('--color-warning', '#BA7517'),
      colorError: cssVar('--color-danger', '#D85A30'),
      colorInfo: cssVar('--color-info', '#3C3489'),

      colorTextBase: cssVar('--color-text-primary', '#1B2440'),
      colorBgBase: cssVar('--color-bg-base', '#FFFFFF'),
      colorBgLayout: cssVar('--color-bg-layout', '#EDEFF4'),
      colorBorder: cssVar('--color-border', '#E1E6F0'),

      fontFamily:
        "'PingFang SC', 'Microsoft YaHei', 'Hiragino Sans GB', system-ui, sans-serif",
      fontSize: 14,

      borderRadius: 8,
      borderRadiusLG: 14,
      borderRadiusSM: 6,

      controlHeight: 36,
      wireframe: false,
    },
    components: {
      Layout: {
        headerBg: cssVar('--color-bg-base', '#FFFFFF'),
        siderBg: tenant.heroGradient[0] ?? '#0A1733',
        bodyBg: cssVar('--color-bg-layout', '#EDEFF4'),
        headerHeight: 62,
      },
      Menu: {
        darkItemBg: 'transparent',
        darkItemSelectedBg: 'rgba(255,255,255,0.12)',
        darkItemHoverBg: 'rgba(255,255,255,0.06)',
        darkItemColor: 'rgba(255,255,255,0.75)',
        darkItemSelectedColor: '#FFFFFF',
      },
      Button: {
        controlHeight: 36,
        fontWeight: 500,
      },
      Card: {
        borderRadiusLG: 14,
        paddingLG: 20,
      },
      Table: {
        cellPaddingBlock: 10,
        cellPaddingInline: 12,
        headerBg: cssVar('--color-bg-soft', '#F4F6F9'),
      },
      Tag: {
        borderRadiusSM: 4,
      },
    },
  };
}
